import React, { ComponentClass, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { ContextualMenuItemType } from "@fluentui/react";
import { useTheme } from "cayo.ui";
import styled from "styled-components";
import Badge from "@cayo/shared/components/Badge";
import useService from "@cayo/shared/services/hooks/useService";
import { CommandBarProps } from ".";
import commandBarButtonIds from "./buttons-ids";
import CommandBarController from "./controller";
import { commandBarMessages } from "./messages";
import Icon from "../Graphics/Icon";

const useCommandBar = (props: CommandBarProps) => {
  const { scheme, gridState } = props;
  const actionBuilder = useService("actionBuilder");
  const intl = useIntl();
  const { officeTheme, cayoTheme } = useTheme();

  const controller = useMemo(() => new CommandBarController(props, actionBuilder), [scheme?.items]);

  const [items, setItems] = useState(controller.items || []);
  const itemsRef = useRef(items);

  useEffect(() => {
    if (gridState?.query || props?.object) {
      controller.update(gridState);
      // logger.debug(
      //   "!ResolveBinding!",
      //   "------------------------------------------------------------"
      // );
    }

    const refreshItem = controller?.items?.find((i) => i?.data?.key === "Refresh");
    if (refreshItem) {
      if (gridState?.hasChanges) {
        refreshItem.commandBarButtonAs = (b) => {
          const DR = b.defaultRender as unknown as ComponentClass;
          refreshItem.title = intl.formatMessage(commandBarMessages.RefreshTitleNewData);

          return (
            <RefreshItem>
              <DR {...b} />
              <Badge
                right={-2}
                top={4}
                width={12}
                height={12}
                position="absolute"
                color={cayoTheme.brandColors.info}
              >
                <ExclamationMark>!</ExclamationMark>
              </Badge>
            </RefreshItem>
          );
        };
      } else {
        delete refreshItem.commandBarButtonAs;
      }
    }

    const commandsWithCompositeIcons = controller?.items?.filter(
      (i) => i?.iconProps?.iconName && i.iconProps.iconName.indexOf("+") > 0
    );

    commandsWithCompositeIcons?.forEach((c) => {
      c.onRenderIcon = (p) => {
        const data = (p as any).data;
        // const styles  = p?.styles as IStyleFunctionOrObject<IIconStyleProps, IIconStyles>;
        // styles.
        return (
          <Icon
            iconName={data?.icon?.iconName}
            style={{
              color: (p as any)?.disabled
                ? cayoTheme.brandColors.disabled
                : officeTheme.semanticColors.menuIcon,
              // eslint-disable-next-line no-inline-styles/no-inline-styles
              marginLeft: 0,
            }}
          />
        );
      };
    });

    // logger.debug(
    //   "!ResolveBinding!",
    //   "------------------------------------------------------------"
    // );

    const newItems = controller.items?.filter(
      (item) =>
        item.key === commandBarButtonIds.maxResults ||
        item.visible !== false ||
        item.itemType === ContextualMenuItemType.Divider
    );

    const updatedItems = newItems || [];
    setItems(updatedItems);

    itemsRef.current = updatedItems;
  }, [gridState?.selectedItems, gridState?.loading, gridState?.query, gridState?.hasChanges]);

  return { items, itemsRef };
};

const ExclamationMark = styled.span`
  font-weight: bold;
`;

const RefreshItem = styled.div`
  position: relative;
  display: flex;
  margin-right: 5px;
`;

export default useCommandBar;
